import * as React from 'react';
import * as classnames from 'classnames';
import { SpinnerIcon } from '@estee/elc-icons';
import styled from 'styled-components';
import { buttonOutlinedTheme, buttonTextTheme, buttonTheme } from '../theme/default-theme';

export type ButtonSize =
    /**
     * Small
     */
    | 'sm'
    /**
     * Large
     */
    | 'lg'
    /**
     * Custom sizes
     */
    | string;

export interface IButtonDisplayProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /**
     * If set to <code>true</code> then the component will span the width of the container
     */
    block?: boolean;
    /**
     * Based on button size a class name will be appended to the class list
     */
    size?: ButtonSize;
    /**
     * @inheritDoc
     */
    className?: string;
}

export type IButton = React.ComponentType<
    React.ButtonHTMLAttributes<HTMLButtonElement> & IButtonDisplayProps
>;

const buttonSizeClass = (sz: string | undefined) => {
    return sz ? `elc-button--${sz}` : '';
};

const getButtonClassNames = (props: IButtonDisplayProps) => {
    return classnames(['elc-button', 'js-button', buttonSizeClass(props.size), props.className]);
};

const Btn = styled.button`
    border: none;
    cursor: pointer;
    letter-spacing: 1.25px;
    font-size: 14px;
    padding: 10px 20px;

    &:disabled {
        cursor: not-allowed;
    }

    ${(props: IButtonDisplayProps) =>
        props.block
            ? `
            display: block;
            width: 100%;
            `
            : ''};
    ${buttonTheme};
    &.${buttonSizeClass('sm')} {
        padding: 4px 8px;
        font-size: 12px;
    }
    /* stylelint-disable */
    &.${buttonSizeClass('lg')} {
        padding: 16px 32px;
    }
    /* stylelint-enable */
`;

export const Button: React.SFC<IButtonDisplayProps> = props => {
    const className = getButtonClassNames(props);

    return (
        <Btn {...props} className={className}>
            {props.children}
        </Btn>
    );
};

export const StyledTextButton = (props: IButtonDisplayProps) => {
    const StyledButton = styled(Btn)`
        ${buttonTextTheme};
    `;
    const className = getButtonClassNames(props);

    return <StyledButton {...props} className={className} />;
};

export const TextButton: React.SFC<IButtonDisplayProps> = props => {
    const { className, ...restOfProps } = props;
    const textButtonClassNames = `${className} elc-button--text`;

    return (
        <StyledTextButton className={textButtonClassNames} {...restOfProps}>
            {props.children}
        </StyledTextButton>
    );
};

export const StyledOutlinedButton = (props: IButtonDisplayProps) => {
    const StyledButton = styled(Btn)`
        border-style: solid;
        border-width: 1px;
        ${buttonOutlinedTheme};
    `;
    const className = getButtonClassNames(props);

    return <StyledButton {...props} className={className} />;
};

export const OutlinedButton: React.SFC<IButtonDisplayProps> = props => {
    const { className, ...restOfProps } = props;
    const outlinedButtonClassNames = `${className} elc-button--outlined`;

    return (
        <StyledOutlinedButton className={outlinedButtonClassNames} {...restOfProps}>
            {props.children}
        </StyledOutlinedButton>
    );
};

export const LoadingButton: IButton = styled(Button).attrs({
    className: 'elc-button-spinner'
})`
    position: relative;
    overflow: hidden;
`;
LoadingButton.displayName = 'LoadingButton';

const SpinnerContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;

export interface IButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        IButtonDisplayProps {
    isLoading?: boolean;
}

const LoadableButton: React.SFC<IButtonProps> = props => (
    <LoadingButton disabled={props.isLoading} {...props}>
        {props.isLoading && (
            <SpinnerContainer>
                <SpinnerIcon />
            </SpinnerContainer>
        )}
        {props.children}
    </LoadingButton>
);

export { LoadableButton };
